// Arabic-aware normalize: collapse common letter variants so users don't have
// to type the exact diacritical form. Examples:
//   "أحمد" == "احمد" == "إحمد", "مدرسة" == "مدرسه", "موسى" == "موسي"
//
// Feb 28, 2026: extended to also fold:
//   • ك (Persian kaf U+06A9) → ك (Arabic kaf U+0643)
//   • ی (Persian ya  U+06CC) → ي
//   • ة (ta marbuta)         → ه (already folded)
//   • ء (standalone hamza)   → removed (so "لؤلؤ" ≈ "لولو", "شاء" ≈ "شا")
//   • ـ (tatweel/kashida)    → removed
//   • zero-width joiners     → removed
//   • ASCII/Arabic punctuation stripped so "لبن، العريس" == "لبن العريس"
export const normAr = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[\u064B-\u0652\u0670\u0640]/g, "") // tashkeel + superscript alef + tatweel
    .replace(/[\u200C\u200D\u200E\u200F\uFEFF]/g, "") // zero-width joiners / marks
    .replace(/[إأآاٱ]/g, "ا")
    .replace(/[ىيی]/g, "ي")
    .replace(/[ةه]/g, "ه")
    .replace(/[ؤو]/g, "و")
    .replace(/[ئي]/g, "ي")
    .replace(/[كک]/g, "ك")
    .replace(/ء/g, "")
    // Arabic-Indic digits → ASCII
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)))
    // Punctuation → space so multi-word queries still segment cleanly
    .replace(/[،.,;:!?()\[\]{}«»""''\-_/\\]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Spoken Arabic number words → numeric. Lets the user type "الف" / "كيلو" /
// "نص كيلو" / "تسعمئه" and match products labeled "1000" / "500" / "900".
// IMPORTANT: patterns below are already letter-normalized (no ة, no ى, no
// hamza variants) so they apply AFTER normAr().
//
// `\b` is unreliable for Arabic in JS regex; we hand-craft a not-letter
// look-around using the Arabic block.
const NB = "(?<![ا-يa-z0-9])"; // not preceded by Arabic letter / latin / digit
const NA = "(?![ا-يa-z0-9])";  // not followed by either
const _word = (alts, replacement) => [new RegExp(`${NB}(?:${alts})${NA}`, "g"), replacement];
const SPOKEN_NUMBERS = [
  _word("ميه|مايه|مئه",                                                     "100"),
  _word("ميتين|مايتين|مئتين",                                                "200"),
  _word("ثلاثميه|ثلاثمايه|ثلثمايه",                                          "300"),
  _word("اربعميه|اربعمايه",                                                  "400"),
  _word("خمسميه|خمسمايه|نص كيلو|نصف كيلو",                                   "500"),
  _word("ستميه|ستمايه",                                                      "600"),
  _word("سبعميه|سبعمايه",                                                    "700"),
  _word("ثمنميه|ثمنمايه|ثمانميه|ثمانمايه",                                    "800"),
  _word("تسعميه|تسعمايه",                                                     "900"),
  _word("الف|كيلو|كغ|كيلوغرام|كيلو غرام",                                     "1000"),
  _word("الفين|كيلوين",                                                       "2000"),
  _word("ثلاث الاف|ثلاثه الاف",                                                "3000"),
  _word("اربع الاف|اربعه الاف",                                                "4000"),
  _word("خمس الاف|خمسه الاف",                                                  "5000"),
];

// Replace each spoken number form with its numeric value. Applied to both
// query AND haystack so the user can type either form — both sides converge
// to the digits. Already letter-normalized input is assumed.
const expandSpokenNumbers = (s) => {
  let out = s;
  for (const [rx, num] of SPOKEN_NUMBERS) {
    out = out.replace(rx, num);
  }
  return out;
};

// Smart, multi-word, order-insensitive matcher. All words in `query` must
// appear (in any order) somewhere in the searchable `text`. Arabic letter
// variants are normalized, common digit forms (٠-٩, ۰-۹) folded into ASCII,
// and spoken-number words mapped to their numeric equivalents — on both
// sides — so typing "الف" matches a product literally labeled "1000".
export const smartMatch = (text, query) => {
  const qNorm = normAr(query);
  if (!qNorm) return true;
  const q = expandSpokenNumbers(qNorm);
  const hay = expandSpokenNumbers(normAr(text));
  const terms = q.split(" ").filter(Boolean);
  return terms.every((t) => hay.includes(t));
};
